import { ErrorMessage, Field, Form, Formik } from "formik";
import Cookies from "js-cookie";
import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { FaEye } from "react-icons/fa";
import { FaEyeSlash } from "react-icons/fa6";
import { FiArrowRight, FiLock, FiUser } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import OtpVerification from "./OtpVerification";
import ResetPasswordPage from "./ResetPasswordPage";
import AppContext from "../../context/AppContext";
import { generateToken } from "../../Push_notification/Notification";
import {
  fetchAndStoreUserPermissions,
  loginUser,
  mapLoginResponseToUser,
  storeFcmToken,
} from "../../service/AuthService";
import { getDefaultRouteFromPermissions } from "../../utils/permissions";
import { getLoginErrorMessage, getLoginResponseErrorMessage } from "../../utils/loginErrorMessages";
import { ensureSuperAdminOrganizationContext } from "../../utils/organizationContext";
import { getTokenExpiryTime, scheduleSessionExpiryCheck } from "../../utils/authSession";
import { RiLoader3Fill } from "react-icons/ri";
import LangComp from "../../components/_app/langComp";
import translations from "../../components/common/Translations";
import { WASL_APP_NAME, WASL_LOGO } from "../../branding/waslBrand";
import DesktopBackendUrlField from "../../components/call-center/DesktopBackendUrlField";

const registrationSchema = (langMode) =>
  Yup.object().shape({
    email: Yup.string()
      .trim()
      .required(translations[langMode].UsernameRequired || "Username is required"),
    password: Yup.string()
      .trim()
      .min(2, translations[langMode].PasswordMustBeAtLeast8Characters)
      .required(translations[langMode].PasswordRequired),
  });

const languageData = [
  { id: 1, lang: "en", langText: "English" },
  { id: 2, lang: "ur", langText: "Urdu" },
  { id: 3, lang: "ar", langText: "Arabic" },
];

const LABEL_CLASS =
  "mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-slate-400";
const FIELD_WRAP_CLASS =
  "flex h-11 overflow-hidden rounded-xl border border-slate-200 bg-white transition focus-within:border-[#2B7FFF] focus-within:ring-2 focus-within:ring-[#2B7FFF]/15";

const LoginPage = () => {
  const { setUser, fetchNotifications, toggleLangMode, langMode, setSelectedOrgId } =
    useContext(AppContext);
  const [resetEmail, setResetEmail] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const navbarRef = useRef(null);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      const trimmedValues = {
        email: (values.email || "").trim(),
        password: (values.password || "").trim(),
      };

      console.log("Submitting login with values:", trimmedValues);
      const responseData = await loginUser(trimmedValues);

      const rememberCheckbox = document.getElementById("remember");
      const isChecked = rememberCheckbox?.checked;

      if (isChecked) {
        window.localStorage.setItem("isLoggedIn", "true");
      }

      if (responseData?.header?.code === 600) {
        const mapped = mapLoginResponseToUser(responseData.body?.value);
        if (!mapped?.accessToken) {
          toast.error("Login failed: missing token in response");
          return;
        }

        const { accessToken, ...userDetails } = mapped;

        setUser(userDetails);
        sessionStorage.setItem("userDetails", JSON.stringify(userDetails));
        localStorage.setItem("userDetails", JSON.stringify(userDetails));
        localStorage.setItem("accessToken", accessToken);

        if (userDetails?.organization?.organizationId) {
          localStorage.setItem("selectedOrgId", userDetails.organization.organizationId);
          localStorage.setItem(
            "selectedOrgName",
            userDetails.organization.organizationName || ""
          );
          setSelectedOrgId(userDetails.organization.organizationId);
        } else if (userDetails?.organizationId) {
          localStorage.setItem("selectedOrgId", String(userDetails.organizationId));
          localStorage.setItem("selectedOrgName", userDetails.organizationName || "");
          setSelectedOrgId(userDetails.organizationId);
        } else if (userDetails?.userType === "super_admin") {
          await ensureSuperAdminOrganizationContext();
          const storedOrgId = localStorage.getItem("selectedOrgId");
          if (storedOrgId) {
            setSelectedOrgId(storedOrgId);
          }
        }

        localStorage.setItem("justLoggedIn", "true");

        const tokenExpiryMs = getTokenExpiryTime(accessToken);
        const expires = tokenExpiryMs
          ? new Date(tokenExpiryMs)
          : new Date(Date.now() + 7 * 60 * 60 * 1000);

        Cookies.set("accessToken", accessToken, { expires });
        Cookies.set("userType", userDetails.userType, { secure: true, sameSite: "strict" });
        if (userDetails.role) {
          Cookies.set("userRole", userDetails.role, { secure: true, sameSite: "strict" });
        }
        Cookies.set("userId", userDetails.userId);
        Cookies.set("userName", userDetails.name);
        if (userDetails.userVisibility != null) {
          Cookies.set("userVisibility", userDetails.userVisibility);
        }
        if (userDetails.profilePic != null) {
          Cookies.set("profilePic", userDetails.profilePic);
        }
        Cookies.set("firstTimeLogin", userDetails.firstTimeLogin);
        Cookies.set("userEmail", userDetails.email);

        await fetchAndStoreUserPermissions(accessToken, { expires });
        scheduleSessionExpiryCheck(() => {
          window.location.assign("/");
        });

        navigate(getDefaultRouteFromPermissions());

        toast.success(
          `${
            userDetails.firstTimeLogin === "false"
              ? translations[langMode].welcomeback
              : translations[langMode].welcome
          } ${userDetails.name}`
        );

        const fcmToken = await generateToken();
        await storeFcmToken(fcmToken, accessToken).catch((err) =>
          console.warn("FCM token store failed:", err)
        );
        fetchNotifications(0);
      } else if (responseData?.header?.code === 613) {
        setResetEmail(trimmedValues.email);
        setResetPassword(true);
      } else {
        const loginError = getLoginResponseErrorMessage(responseData, langMode);
        if (loginError) {
          toast.error(loginError);
        }
      }
    } catch (error) {
      console.error("Login Error:", error);
      toast.error(getLoginErrorMessage(error, langMode));
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setIsNavbarOpen(false);
      }
    };

    if (isNavbarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isNavbarOpen]);

  const validationSchema = useMemo(() => registrationSchema(langMode), [langMode]);
  const isRtl = langMode === "ar";

  return (
    <>
      {!resetEmail && !resetPassword && (
        <div className="flex min-h-screen min-h-[100vh] flex-col overflow-x-hidden lg:flex-row">
          {/* Left hero */}
          <aside className="relative flex w-full flex-col justify-between overflow-hidden bg-gradient-to-b from-[#1D4ED8] to-[#1E3A8A] px-8 py-8 text-white sm:px-10 lg:w-[48%] lg:px-12 lg:py-10 xl:px-14">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.18),_transparent_55%),radial-gradient(ellipse_at_bottom_left,_rgba(15,60,140,0.35),_transparent_50%)]" />
            <div className="pointer-events-none absolute -right-20 top-1/4 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="pointer-events-none absolute -left-16 bottom-0 h-64 w-64 rounded-full bg-[#0b3d8c]/40 blur-3xl" />

            <div className="relative z-10 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white p-1.5 shadow-md">
                <img src={WASL_LOGO} alt="WasL logo" className="h-full w-full object-contain" />
              </div>
              <span className="text-xl font-semibold tracking-tight text-white">{WASL_APP_NAME}</span>
            </div>

            <div className="relative z-10 mt-12 max-w-md lg:mt-auto">
              <h1 className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl xl:text-[2.75rem] xl:leading-[1.15]">
                Recover more, call smarter.
              </h1>
              <p className="mt-4 max-w-sm text-sm leading-6 text-white/80 sm:text-[15px] sm:leading-7">
                The EEMAD call-centre workspace for outbound dues recovery — targets, campaigns,
                and live tracking in one clean view.
              </p>

              <div className="mt-10 grid grid-cols-2 gap-8">
                <div>
                  <p className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
                    OMR 84.7K
                  </p>
                  <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/55">
                    Recovered this month
                  </p>
                </div>
                <div>
                  <p className="text-2xl font-bold tracking-tight text-white sm:text-3xl">3,272</p>
                  <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/55">
                    Active targets
                  </p>
                </div>
              </div>

              <p className="mt-10 text-xs text-white/50">
                Powered by{" "}
                <a
                  href="https://www.outbox.om/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-white/80 underline-offset-2 transition hover:text-white hover:underline"
                >
                  Outbox
                </a>
              </p>
            </div>
          </aside>

          {/* Right form */}
          <main className="relative flex w-full flex-1 flex-col bg-[#f9fafb] px-5 py-8 sm:px-8 lg:w-[52%] lg:px-12 lg:py-10">
            <div className="absolute right-5 top-6 sm:right-8 lg:right-10" ref={navbarRef}>
              <LangComp
                isShowIcon
                languageDataList={languageData}
                toggleLangMode={toggleLangMode}
                isNavbarOpen={isNavbarOpen}
              />
            </div>

            <div className="flex flex-1 items-center justify-center">
              <div className="w-full max-w-[420px]">
                <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8">
                  <span className="inline-flex items-center gap-2 rounded-full bg-[#EEF4FF] px-3 py-1 text-xs font-semibold text-[#2B7FFF]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#2B7FFF]" />
                    Call admin sign in
                  </span>

                  <h2 className="mt-4 text-[28px] font-bold tracking-tight text-[#111827]">
                    Welcome back
                  </h2>
                  <p className="mt-1.5 text-sm text-[#6b7280]">
                    Sign in to your EEMAD workspace.
                  </p>

                  <DesktopBackendUrlField compact />

                  <Formik
                    initialValues={{ email: "", password: "" }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ isSubmitting }) => (
                      <Form className="mt-7 space-y-5">
                        <div>
                          <label className={LABEL_CLASS}>
                            {translations[langMode]?.UsernameEmailAddress || "Email"}
                          </label>
                          <div
                            className={`${FIELD_WRAP_CLASS} ${isRtl ? "flex-row-reverse" : ""}`}
                          >
                            <span className="flex w-11 shrink-0 items-center justify-center text-slate-400">
                              <FiUser className="h-4 w-4" />
                            </span>
                            <Field
                              type="text"
                              name="email"
                              autoComplete="username"
                              placeholder="vivek@eemad.om"
                              className="h-full min-w-0 flex-1 border-0 bg-transparent pr-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none"
                            />
                          </div>
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="mt-1.5 text-xs text-red-500"
                          />
                        </div>

                        <div>
                          <label className={LABEL_CLASS}>
                            {translations[langMode]?.password || "Password"}
                          </label>
                          <div
                            className={`${FIELD_WRAP_CLASS} ${isRtl ? "flex-row-reverse" : ""}`}
                          >
                            <span className="flex w-11 shrink-0 items-center justify-center text-slate-400">
                              <FiLock className="h-4 w-4" />
                            </span>
                            <Field
                              type={showPassword ? "text" : "password"}
                              name="password"
                              autoComplete="current-password"
                              placeholder="••••••••"
                              className="h-full min-w-0 flex-1 border-0 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none"
                            />
                            <button
                              type="button"
                              onClick={togglePasswordVisibility}
                              className="flex w-11 shrink-0 items-center justify-center text-slate-400 transition hover:text-[#2B7FFF]"
                              aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                          <ErrorMessage
                            name="password"
                            component="div"
                            className="mt-1.5 text-xs text-red-500"
                          />
                        </div>

                        <div className="flex items-center justify-between gap-3">
                          <label className="flex cursor-pointer items-center gap-2.5 text-sm font-medium text-slate-600">
                            <input
                              type="checkbox"
                              id="remember"
                              className="h-4 w-4 rounded border-slate-300 text-[#2B7FFF] accent-[#2B7FFF] focus:ring-[#2B7FFF]"
                            />
                            {translations[langMode]?.RememberMe || "Remember me"}
                          </label>
                          <Link
                            to="/reset-password"
                            className="text-sm font-semibold text-[#2B7FFF] transition hover:text-[#1d6ef2]"
                          >
                            {translations[langMode]?.ForgotPassword || "Forgot password?"}
                          </Link>
                        </div>

                        <button
                          type="submit"
                          disabled={isSubmitting || loading}
                          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#2B7FFF] text-sm font-semibold text-white shadow-sm transition hover:bg-[#1d6ef2] disabled:opacity-60"
                        >
                          {loading ? (
                            <RiLoader3Fill className="animate-spin text-xl" />
                          ) : (
                            <>
                              {translations[langMode]?.Login?.trim() || "Sign in"}
                              <FiArrowRight className="h-4 w-4" aria-hidden="true" />
                            </>
                          )}
                        </button>
                      </Form>
                    )}
                  </Formik>
                </div>

                <p className="mt-6 text-center text-xs leading-5 text-slate-400">
                  Protected workspace · Contact your administrator for access.
                </p>
              </div>
            </div>
          </main>
        </div>
      )}

      {resetEmail && !resetPassword && <OtpVerification emaill={resetEmail} />}

      {resetPassword && <ResetPasswordPage emaill={resetEmail} />}
    </>
  );
};

export default LoginPage;
